import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, 
  List, Plus, Clock, Euro, Shield, FileText, Zap, Check,
  X, Edit2, Trash2, AlertCircle, CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPI_SCADENZA = {
  evento: { label: "Evento", colore: "#3B82F6", icon: CalendarIcon },
  pagamento: { label: "Pagamento/Quota", colore: "#22C55E", icon: Euro },
  assicurazione: { label: "Assicurazione", colore: "#F97316", icon: Shield },
  documento: { label: "Documento/Comunicazione", colore: "#A855F7", icon: FileText },
  utenza: { label: "Utenza/Fornitore", colore: "#EF4444", icon: Zap },
  altro: { label: "Altro", colore: "#6B7280", icon: Clock }
};

const RICORRENZE = {
  una_tantum: "Una tantum",
  mensile: "Mensile",
  trimestrale: "Trimestrale",
  semestrale: "Semestrale",
  annuale: "Annuale"
};

export default function CalendarioScadenzario() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('calendar');
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [prossimeScadenze, setProssimeScadenze] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    titolo: '',
    descrizione: '',
    data_scadenza: '',
    tipo: 'altro',
    importo: '',
    ricorrenza: 'una_tantum',
    note: ''
  });

  const daysOfWeek = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
  const months = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  useEffect(() => {
    fetchCalendario();
    fetchProssimeScadenze();
  }, [currentDate]);

  const fetchCalendario = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const anno = currentDate.getFullYear();
      const mese = currentDate.getMonth() + 1;
      
      const response = await fetch(`${API_URL}/api/scadenze/calendario?anno=${anno}&mese=${mese}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setItems(data.items || []);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Errore nel caricamento');
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore di connessione');
    } finally {
      setLoading(false);
    }
  };

  const fetchProssimeScadenze = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/scadenze/prossime?giorni=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProssimeScadenze(data || []);
      }
    } catch (error) {
      console.error('Errore prossime scadenze:', error);
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    let startDay = firstDay.getDay();
    startDay = startDay === 0 ? 6 : startDay - 1;
    
    const days = [];
    
    for (let i = 0; i < startDay; i++) {
      days.push({ day: null, items: [] });
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayItems = items.filter(item => item.data_scadenza === dateStr);
      days.push({ day, date: dateStr, items: dayItems });
    }
    
    return days;
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const openNewDialog = (date = null) => {
    setEditingItem(null);
    setFormData({
      titolo: '',
      descrizione: '',
      data_scadenza: date || new Date().toISOString().split('T')[0],
      tipo: 'altro',
      importo: '',
      ricorrenza: 'una_tantum',
      note: ''
    });
    setShowDialog(true);
  };

  const openEditDialog = (item) => {
    if (item.auto_generated) {
      toast.info('Questa scadenza è generata automaticamente e non può essere modificata');
      return;
    }
    setEditingItem(item);
    setFormData({
      titolo: item.titolo || '',
      descrizione: item.descrizione || '',
      data_scadenza: item.data_scadenza || '',
      tipo: item.tipo || 'altro',
      importo: item.importo || '',
      ricorrenza: item.ricorrenza || 'una_tantum',
      note: item.note || ''
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.titolo.trim()) {
      toast.error('Inserisci un titolo');
      return;
    }
    if (!formData.data_scadenza) {
      toast.error('Seleziona una data');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = editingItem 
        ? `${API_URL}/api/scadenze/${editingItem.id}`
        : `${API_URL}/api/scadenze`;
      
      const body = {
        ...formData,
        importo: formData.importo ? parseFloat(formData.importo) : null
      };

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success(editingItem ? 'Scadenza aggiornata' : 'Scadenza creata');
        setShowDialog(false);
        fetchCalendario();
        fetchProssimeScadenze();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa scadenza?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/scadenze/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Scadenza eliminata');
        fetchCalendario();
        fetchProssimeScadenze();
      } else {
        toast.error('Errore nell\'eliminazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handleToggleCompleta = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/scadenze/${id}/completa`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Stato aggiornato');
        fetchCalendario();
        fetchProssimeScadenze();
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const days = getDaysInMonth();

  return (
    <div className="space-y-6" data-testid="calendario-scadenzario-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Calendario Scadenzario</h1>
          <p className="text-slate-500">Gestisci tutte le scadenze in un unico posto</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'calendar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('calendar')}
          >
            <CalendarIcon className="w-4 h-4 mr-1" />
            Calendario
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="w-4 h-4 mr-1" />
            Lista
          </Button>
          <Button onClick={() => openNewDialog()}>
            <Plus className="w-4 h-4 mr-1" />
            Nuova Scadenza
          </Button>
        </div>
      </div>

      {/* Legenda */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            {Object.entries(TIPI_SCADENZA).map(([key, value]) => {
              const Icon = value.icon;
              return (
                <div key={key} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: value.colore }}
                  />
                  <Icon className="w-4 h-4" style={{ color: value.colore }} />
                  <span className="text-sm text-slate-600">{value.label}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Calendario / Lista */}
        <div className="lg:col-span-2">
          {viewMode === 'calendar' ? (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={handlePrevMonth}>
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <h2 className="text-xl font-semibold min-w-[200px] text-center">
                      {months[currentDate.getMonth()]} {currentDate.getFullYear()}
                    </h2>
                    <Button variant="ghost" size="icon" onClick={handleNextMonth}>
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleToday}>
                    Oggi
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="h-96 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </div>
                ) : (
                  <div className="grid grid-cols-7 gap-1">
                    {/* Header giorni */}
                    {daysOfWeek.map(day => (
                      <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
                        {day}
                      </div>
                    ))}
                    
                    {/* Giorni del mese */}
                    {days.map((dayData, idx) => (
                      <div
                        key={idx}
                        className={`min-h-[100px] p-1 border rounded-lg cursor-pointer transition-colors
                          ${dayData.day ? 'hover:bg-slate-50' : 'bg-slate-50/50'}
                          ${dayData.date === today ? 'ring-2 ring-blue-500 bg-blue-50' : ''}
                        `}
                        onClick={() => dayData.day && openNewDialog(dayData.date)}
                      >
                        {dayData.day && (
                          <>
                            <div className={`text-sm font-medium mb-1 ${dayData.date === today ? 'text-blue-600' : 'text-slate-700'}`}>
                              {dayData.day}
                            </div>
                            <div className="space-y-1">
                              {dayData.items.slice(0, 3).map((item, i) => (
                                <div
                                  key={i}
                                  className={`text-xs p-1 rounded truncate ${item.completata ? 'opacity-50 line-through' : ''}`}
                                  style={{ backgroundColor: `${item.colore}20`, color: item.colore }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditDialog(item);
                                  }}
                                  title={item.titolo}
                                >
                                  {item.titolo}
                                </div>
                              ))}
                              {dayData.items.length > 3 && (
                                <div className="text-xs text-slate-500 pl-1">
                                  +{dayData.items.length - 3} altri
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Tutte le Scadenze - {months[currentDate.getMonth()]} {currentDate.getFullYear()}</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="h-48 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </div>
                ) : items.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Nessuna scadenza questo mese</p>
                ) : (
                  <div className="space-y-3">
                    {items.map((item) => {
                      const Icon = TIPI_SCADENZA[item.tipo]?.icon || Clock;
                      return (
                        <div
                          key={item.id}
                          className={`flex items-center justify-between p-3 rounded-lg border hover:shadow-sm transition-shadow
                            ${item.completata ? 'bg-slate-50 opacity-60' : 'bg-white'}
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: `${item.colore}20` }}
                            >
                              <Icon className="w-5 h-5" style={{ color: item.colore }} />
                            </div>
                            <div>
                              <div className={`font-medium ${item.completata ? 'line-through' : ''}`}>
                                {item.titolo}
                              </div>
                              <div className="text-sm text-slate-500 flex items-center gap-2">
                                <span>{new Date(item.data_scadenza).toLocaleDateString('it-IT')}</span>
                                {item.importo && (
                                  <Badge variant="outline">€{item.importo.toFixed(2)}</Badge>
                                )}
                                {item.auto_generated && (
                                  <Badge variant="secondary" className="text-xs">Auto</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!item.auto_generated && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => handleToggleCompleta(item.id)}
                                  title={item.completata ? 'Segna come da fare' : 'Segna come completata'}
                                >
                                  {item.completata ? (
                                    <X className="w-4 h-4 text-slate-400" />
                                  ) : (
                                    <Check className="w-4 h-4 text-green-600" />
                                  )}
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => openEditDialog(item)}
                                >
                                  <Edit2 className="w-4 h-4 text-slate-400" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => handleDelete(item.id)}
                                >
                                  <Trash2 className="w-4 h-4 text-red-400" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - Prossime Scadenze */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-500" />
                Prossime 30 giorni
              </CardTitle>
            </CardHeader>
            <CardContent>
              {prossimeScadenze.length === 0 ? (
                <p className="text-slate-500 text-sm py-4 text-center">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  Nessuna scadenza imminente
                </p>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {prossimeScadenze.slice(0, 10).map((item) => {
                    const daysLeft = Math.ceil(
                      (new Date(item.data_scadenza) - new Date()) / (1000 * 60 * 60 * 24)
                    );
                    return (
                      <div
                        key={item.id}
                        className="p-2 rounded border-l-4 bg-slate-50"
                        style={{ borderColor: item.colore }}
                      >
                        <div className="font-medium text-sm truncate">{item.titolo}</div>
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>{new Date(item.data_scadenza).toLocaleDateString('it-IT')}</span>
                          <span className={daysLeft <= 7 ? 'text-red-600 font-medium' : ''}>
                            {daysLeft === 0 ? 'Oggi' : daysLeft === 1 ? 'Domani' : `${daysLeft} giorni`}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Riepilogo Mese</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(TIPI_SCADENZA).map(([key, value]) => {
                  const count = items.filter(i => i.tipo === key).length;
                  if (count === 0) return null;
                  return (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">{value.label}</span>
                      <Badge style={{ backgroundColor: value.colore }}>{count}</Badge>
                    </div>
                  );
                })}
                {items.length === 0 && (
                  <p className="text-slate-400 text-sm text-center">Nessuna scadenza</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialog Nuova/Modifica Scadenza */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Modifica Scadenza' : 'Nuova Scadenza'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Titolo *</Label>
              <Input
                value={formData.titolo}
                onChange={(e) => setFormData({...formData, titolo: e.target.value})}
                placeholder="Es: Scadenza polizza RC"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Scadenza *</Label>
                <Input
                  type="date"
                  value={formData.data_scadenza}
                  onChange={(e) => setFormData({...formData, data_scadenza: e.target.value})}
                />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select 
                  value={formData.tipo} 
                  onValueChange={(v) => setFormData({...formData, tipo: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TIPI_SCADENZA).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: value.colore }}
                          />
                          {value.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Importo (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.importo}
                  onChange={(e) => setFormData({...formData, importo: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Ricorrenza</Label>
                <Select 
                  value={formData.ricorrenza} 
                  onValueChange={(v) => setFormData({...formData, ricorrenza: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(RICORRENZE).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Descrizione</Label>
              <Textarea
                value={formData.descrizione}
                onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                placeholder="Dettagli aggiuntivi..."
                rows={2}
              />
            </div>

            <div>
              <Label>Note</Label>
              <Textarea
                value={formData.note}
                onChange={(e) => setFormData({...formData, note: e.target.value})}
                placeholder="Note interne..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSave}>
              {editingItem ? 'Salva Modifiche' : 'Crea Scadenza'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
